import React, {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';
import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders children in a horizontally scrolling loop (marquee effect).
 * Content is duplicated, so the animation loops seamlessly. Runs entirely
 * on the UI thread via Reanimated.
 *
 * @example
 * ```tsx
 * <RunningLine speed={60} gap={24}>
 *   <Text>FRESH INGREDIENTS</Text>
 *   <Text>•</Text>
 *   <Text>ORDER NOW</Text>
 * </RunningLine>
 * ```
 */
export const RunningLine = (params: Props) => {
  const { children, speed = DEFAULT_SPEED, gap = DEFAULT_GAP, paused } = params;

  // ─── State ───────────────────────────────────────────────────────────

  const [contentWidth, setContentWidth] = useState(0);

  // ─── Shared Values ─────────────────────────────────────────────────

  const translateX = useSharedValue(0);

  // ─── Styles ────────────────────────────────────────────────────────

  const trackAnimatedStyle = useAnimatedStyle(() => ({ transform: [{ translateX: translateX.value }] })); // prettier-ignore
  const trackGap = gap * 2;
  const trackDynamicStyles = useStyle(() => ({ gap: trackGap }), [trackGap]);
  const trackStyles = [styles.track, trackDynamicStyles, trackAnimatedStyle];

  const contentContainerDynamicStyles = useStyle(() => ({ gap }), [gap]);
  const contentContainerStyles = [styles.content, contentContainerDynamicStyles]; // prettier-ignore

  // ─── Handlers ──────────────────────────────────────────────────────

  const onContentLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;

    if (width === 0) return;

    setContentWidth(width);
  }, []);

  const resetAnimation = useCallback(() => {
    cancelAnimation(translateX);

    translateX.value = 0;
  }, [translateX]);

  const toggleAnimation = useCallback(() => {
    const shouldResetAnimation = paused || contentWidth === 0;

    if (shouldResetAnimation) {
      resetAnimation();

      return;
    }

    const totalWidth = contentWidth + trackGap;
    const duration = (totalWidth / speed) * 1000;

    translateX.value = 0;
    translateX.value = withRepeat(
      withTiming(-totalWidth, { duration, easing: Easing.linear }),
      -1,
    );
  }, [contentWidth, paused, resetAnimation, speed, trackGap, translateX]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(toggleAnimation, [toggleAnimation]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <Animated.View style={trackStyles}>
        <View style={contentContainerStyles} onLayout={onContentLayout}>
          {children}
        </View>

        {contentWidth > 0 ? (
          <View style={contentContainerStyles}>{children}</View>
        ) : null}
      </Animated.View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  track: {
    flexDirection: 'row',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_SPEED = 60;
const DEFAULT_GAP = theme.spacing['4'];

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  speed?: number;
  gap?: number;
  paused?: boolean;
}>;
